import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Dumbbell,
  Clock,
  X,
  Star,
  Send,
  Minus,
  Pause,
  Home,
  Trophy,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useActiveSession, type StepPerformance } from "@/hooks/useActiveSession";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { AdaptiveSuggestionBanner } from "@/components/dashboard/AdaptiveSuggestionBanner";
import { useAdaptiveSuggestions } from "@/hooks/useAdaptiveSuggestions";
import type { AdaptiveContext } from "@/lib/adaptiveSuggestionEngine";

export default function ActiveSessionOverlay() {
  const {
    session,
    isOpen,
    closeOverlay,
    steps,
    currentStep,
    setCurrentStep,
    toggleStepDone,
    updateStepPerformance,
    startedAt,
    logId,
    setLogId,
    endSession,
    adaptiveSignals,
  } = useActiveSession();
  const { user } = useAuth();
  const navigate = useNavigate();

  const fatigueScore = adaptiveSignals?.fatigueScore;
  const sleepLast = adaptiveSignals?.sleepLast;
  const painCount = adaptiveSignals?.painCount;
  const hasSignals = fatigueScore != null || sleepLast != null || (painCount != null && painCount > 0);
  const sessionAdaptiveCtx: AdaptiveContext = {
    missedYesterday: false,
    todayHasSession: true,
    fatigueScore: fatigueScore ?? null,
    painCount: painCount ?? 0,
    consecutiveTrainingDays: 0,
    sleepLast: sleepLast ?? null,
    missedSessionDays: 0,
    weekDayIndex: 0,
  };
  const { suggestions: sessionSuggestions, dismiss: dismissAdaptive } = useAdaptiveSuggestions(
    hasSignals ? sessionAdaptiveCtx : { missedYesterday: false, todayHasSession: false, fatigueScore: null, painCount: 0, consecutiveTrainingDays: 0, sleepLast: null, missedSessionDays: 0, weekDayIndex: 0 }
  );

  const [phase, setPhase] = useState<"tracking" | "feedback" | "completed">("tracking");
  const [elapsed, setElapsed] = useState(0);
  const [feedback, setFeedback] = useState({ rating: 3, difficulty: 3, fatigue: 3, comment: "" });
  const [finalElapsed, setFinalElapsed] = useState(0);
  const [finalDoneCount, setFinalDoneCount] = useState(0);
  const [finalTotal, setFinalTotal] = useState(0);
  const [saving, setSaving] = useState(false);
  const [confirmAbandon, setConfirmAbandon] = useState(false);

  // Timer
  useEffect(() => {
    if (!startedAt || !isOpen) return;
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startedAt, isOpen]);

  // Reset phase when session changes
  useEffect(() => {
    setPhase("tracking");
    setFeedback({ rating: 3, difficulty: 3, fatigue: 3, comment: "" });
    setFinalElapsed(0);
    setFinalDoneCount(0);
    setFinalTotal(0);
  }, [session?.id]);

  // Create log on start
  useEffect(() => {
    if (!session || !user || logId) return;
    (async () => {
      const { data, error } = await supabase
        .from("session_logs")
        .insert({
          user_id: user.id,
          session_id: session.id,
          status: "in_progress",
          step_data: steps as any,
        } as any)
        .select("id")
        .single();
      if (data) setLogId((data as any).id);
      if (error) console.error("Create session log error:", error);
    })();
  }, [session, user, logId, setLogId]);

  if (!session || !isOpen) return null;

  const exercises = session.exercises || [];
  const doneCount = steps.filter((s) => s.done).length;
  const progress = exercises.length > 0 ? (doneCount / exercises.length) * 100 : 0;
  const allDone = doneCount === exercises.length && exercises.length > 0;

  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handleFinish = () => setPhase("feedback");

  const handleSubmitFeedback = async () => {
    if (!user || !logId) return;
    setSaving(true);
    const durationSeconds = startedAt ? Math.floor((Date.now() - startedAt.getTime()) / 1000) : null;
    const { error } = await supabase
      .from("session_logs")
      .update({
        status: "completed",
        ended_at: new Date().toISOString(),
        step_data: steps as any,
        feedback: feedback as any,
        duration_seconds: durationSeconds,
      } as any)
      .eq("id", logId);

    setSaving(false);
    if (error) {
      toast.error("Erreur lors de l'enregistrement");
      console.error(error);
    } else {
      // Auto-log exercise performances to progression_metrics
      const metricsToInsert = steps
        .filter((s) => s.done && (s.performances.weight || s.performances.reps))
        .map((s) => {
          const hasWeight = s.performances.weight && parseFloat(s.performances.weight) > 0;
          return {
            user_id: user!.id,
            metric_name: `${s.exerciseName}${hasWeight ? " (kg)" : " (reps)"}`,
            value: hasWeight
              ? parseFloat(s.performances.weight!)
              : parseFloat(s.performances.reps!),
            unit: hasWeight ? "kg" : "reps",
          };
        });
      if (metricsToInsert.length > 0) {
        await supabase.from("progression_metrics").insert(metricsToInsert as any);
      }

      // Capture final stats before endSession clears them
      setFinalElapsed(elapsed);
      setFinalDoneCount(doneCount);
      setFinalTotal(exercises.length);
      window.dispatchEvent(new CustomEvent("session-completed", { detail: { sessionId: session?.id } }));
      setPhase("completed");
    }
  };

  const handleAbandon = async () => {
    if (logId) {
      await supabase
        .from("session_logs")
        .update({ status: "abandoned", ended_at: new Date().toISOString(), step_data: steps as any } as any)
        .eq("id", logId);
    }
    setConfirmAbandon(false);
    endSession();
    toast("Séance abandonnée");
  };

  // Minimize = pause (close overlay but keep session alive)
  const handleMinimize = () => {
    closeOverlay();
  };

  const currentExercise = exercises[currentStep];

  return (
    <AnimatePresence>
      {/* Backdrop */}
      <motion.div
        key="session-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9990] bg-black/60 backdrop-blur-sm"
        onClick={handleMinimize}
      />

      {/* Centered panel */}
      <motion.div
        key="session-panel"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="fixed z-[9991] inset-x-4 top-[5vh] bottom-[5vh] mx-auto flex max-w-lg flex-col rounded-2xl border border-border bg-card shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-4 pt-4 pb-2 border-b border-border shrink-0">
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-bold truncate text-foreground">{session.title}</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge variant="outline" className="text-[10px] gap-1">
                <Clock className="h-2.5 w-2.5" />
                {formatTime(elapsed)}
              </Badge>
              <Badge variant="outline" className="text-[10px]">
                {doneCount}/{exercises.length}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleMinimize} title="Réduire (pause)">
              <Minus className="h-3.5 w-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => setConfirmAbandon(true)} title="Quitter la séance">
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        <Progress value={progress} className="h-1.5" />

        {phase === "tracking" ? (
          <>
            {/* Exercise list / current exercise */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {/* Adaptive suggestions (session-relevant only) */}
              {sessionSuggestions.length > 0 && (
                <AdaptiveSuggestionBanner suggestions={sessionSuggestions} onDismiss={dismissAdaptive} />
              )}
              {/* Step pills */}
              <div className="flex gap-1.5 overflow-x-auto pb-1">
                {exercises.map((ex: any, i: number) => (
                  <button
                    key={i}
                    onClick={() => setCurrentStep(i)}
                    className={cn(
                      "shrink-0 flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium border transition-all",
                      currentStep === i
                        ? "border-primary bg-primary/10 text-primary"
                        : steps[i]?.done
                          ? "border-primary/30 bg-primary/5 text-primary/70"
                          : "border-border bg-card text-muted-foreground"
                    )}
                  >
                    {steps[i]?.done ? <Check className="h-2.5 w-2.5" /> : <span>{i + 1}</span>}
                  </button>
                ))}
              </div>

              {/* Current exercise detail */}
              {currentExercise && (
                <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-xs font-bold text-primary">
                      {currentStep + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-sm text-foreground">{currentExercise.name}</h3>
                      <div className="flex gap-2 text-[11px] text-muted-foreground mt-0.5">
                        {currentExercise.sets && <span>Séries: {currentExercise.sets}</span>}
                        {currentExercise.reps && <span>Reps: {currentExercise.reps}</span>}
                        {currentExercise.rest && <span>Repos: {currentExercise.rest}</span>}
                      </div>
                    </div>
                  </div>

                  {currentExercise.notes && (
                    <p className="text-xs text-muted-foreground bg-muted/30 rounded-lg px-3 py-2">{currentExercise.notes}</p>
                  )}

                  {/* Performance inputs */}
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-[10px] text-muted-foreground font-medium">Charge (kg)</label>
                      <Input
                        type="number"
                        placeholder="—"
                        className="h-8 text-xs mt-0.5"
                        value={steps[currentStep]?.performances.weight || ""}
                        onChange={(e) => updateStepPerformance(currentStep, { weight: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-muted-foreground font-medium">Reps</label>
                      <Input
                        type="number"
                        placeholder="—"
                        className="h-8 text-xs mt-0.5"
                        value={steps[currentStep]?.performances.reps || ""}
                        onChange={(e) => updateStepPerformance(currentStep, { reps: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-muted-foreground font-medium">Séries</label>
                      <Input
                        type="number"
                        placeholder="—"
                        className="h-8 text-xs mt-0.5"
                        value={steps[currentStep]?.performances.sets || ""}
                        onChange={(e) => updateStepPerformance(currentStep, { sets: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-muted-foreground font-medium">Temps (sec)</label>
                      <Input
                        type="number"
                        placeholder="—"
                        className="h-8 text-xs mt-0.5"
                        value={steps[currentStep]?.performances.time || ""}
                        onChange={(e) => updateStepPerformance(currentStep, { time: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-muted-foreground font-medium">Distance (m)</label>
                      <Input
                        type="number"
                        placeholder="—"
                        className="h-8 text-xs mt-0.5"
                        value={steps[currentStep]?.performances.distance || ""}
                        onChange={(e) => updateStepPerformance(currentStep, { distance: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-muted-foreground font-medium">RPE (1-10)</label>
                      <Input
                        type="number"
                        min={1}
                        max={10}
                        placeholder="—"
                        className="h-8 text-xs mt-0.5"
                        value={steps[currentStep]?.performances.rpe || ""}
                        onChange={(e) => updateStepPerformance(currentStep, { rpe: Number(e.target.value) || undefined })}
                      />
                    </div>
                  </div>

                  <Textarea
                    placeholder="Notes sur cet exercice…"
                    className="text-xs min-h-[48px]"
                    value={steps[currentStep]?.performances.notes || ""}
                    onChange={(e) => updateStepPerformance(currentStep, { notes: e.target.value })}
                  />

                  {/* Done toggle */}
                  <Button
                    className="w-full gap-2"
                    variant={steps[currentStep]?.done ? "outline" : "default"}
                    onClick={() => {
                      toggleStepDone(currentStep);
                      if (!steps[currentStep]?.done) {
                        const nextUndone = steps.findIndex((s, i) => i > currentStep && !s.done);
                        if (nextUndone >= 0) setTimeout(() => setCurrentStep(nextUndone), 300);
                      }
                    }}
                  >
                    {steps[currentStep]?.done ? (
                      <><Check className="h-4 w-4" /> Fait ✓</>
                    ) : (
                      <><Dumbbell className="h-4 w-4" /> Valider cet exercice</>
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Footer navigation */}
            <div className="shrink-0 border-t border-border px-4 py-3 space-y-2">
              {/* Confirm abandon */}
              {confirmAbandon && (
                <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
                  <span className="flex-1 text-xs text-destructive">Abandonner la séance ?</span>
                  <Button size="sm" variant="destructive" className="h-7 text-xs px-2" onClick={handleAbandon}>Oui</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs px-2" onClick={() => setConfirmAbandon(false)}>Non</Button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={currentStep === 0}
                  onClick={() => setCurrentStep(currentStep - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex-1 text-center text-xs text-muted-foreground">
                  Exercice {currentStep + 1} / {exercises.length}
                </div>
                {allDone || currentStep === exercises.length - 1 ? (
                  <Button size="sm" onClick={handleFinish} className="gap-1">
                    Terminer <Send className="h-3 w-3" />
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={currentStep >= exercises.length - 1}
                    onClick={() => setCurrentStep(currentStep + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {/* Early finish shortcut — visible when ≥1 exercise done but not at last step */}
              {doneCount > 0 && !allDone && currentStep < exercises.length - 1 && (
                <button
                  onClick={handleFinish}
                  className="w-full text-center text-[11px] text-muted-foreground hover:text-foreground transition-colors py-0.5"
                >
                  Terminer la séance maintenant ({doneCount}/{exercises.length})
                </button>
              )}
            </div>
          </>
        ) : phase === "feedback" ? (
          /* Feedback phase */
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
            <div className="text-center space-y-1">
              <h2 className="text-lg font-bold text-foreground">Séance terminée 🎉</h2>
              <p className="text-xs text-muted-foreground">
                {formatTime(elapsed)} · {doneCount}/{exercises.length} exercices
              </p>
            </div>

            <div className="space-y-4 rounded-xl border border-border bg-card p-4">
              <div>
                <label className="text-xs font-medium text-foreground">Ressenti global</label>
                <div className="flex items-center gap-2 mt-1.5">
                  {[1, 2, 3, 4, 5].map((v) => (
                    <button
                      key={v}
                      onClick={() => setFeedback((f) => ({ ...f, rating: v }))}
                      className="transition-transform hover:scale-110"
                    >
                      <Star
                        className={cn(
                          "h-6 w-6 transition-colors",
                          v <= feedback.rating ? "fill-primary text-primary" : "text-muted-foreground/30"
                        )}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-foreground">Difficulté perçue (1-10)</label>
                <Slider
                  min={1}
                  max={10}
                  step={1}
                  value={[feedback.difficulty]}
                  onValueChange={([v]) => setFeedback((f) => ({ ...f, difficulty: v }))}
                  className="mt-2"
                />
                <p className="text-[10px] text-muted-foreground text-right mt-0.5">{feedback.difficulty}/10</p>
              </div>

              <div>
                <label className="text-xs font-medium text-foreground">Fatigue ressentie (1-10)</label>
                <Slider
                  min={1}
                  max={10}
                  step={1}
                  value={[feedback.fatigue]}
                  onValueChange={([v]) => setFeedback((f) => ({ ...f, fatigue: v }))}
                  className="mt-2"
                />
                <p className="text-[10px] text-muted-foreground text-right mt-0.5">{feedback.fatigue}/10</p>
              </div>

              <div>
                <label className="text-xs font-medium text-foreground">Commentaire (optionnel)</label>
                <Textarea
                  placeholder="Comment s'est passée cette séance ?"
                  className="mt-1.5 text-xs min-h-[60px]"
                  value={feedback.comment}
                  onChange={(e) => setFeedback((f) => ({ ...f, comment: e.target.value }))}
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setPhase("tracking")}>
                Retour
              </Button>
              <Button className="flex-1 gap-1" onClick={handleSubmitFeedback} disabled={saving}>
                {saving ? "Enregistrement…" : "Enregistrer"}
                <Send className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ) : (
          /* Completed phase — success screen */
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 space-y-6">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", damping: 12, stiffness: 200 }}
              className="rounded-full bg-primary/10 p-5"
            >
              <Trophy className="h-10 w-10 text-primary" />
            </motion.div>

            <div className="text-center space-y-1.5">
              <h2 className="text-xl font-bold text-foreground">Bien joué ! 💪</h2>
              <p className="text-sm text-muted-foreground">
                Séance enregistrée avec succès
              </p>
              <p className="text-xs text-muted-foreground">
                {formatTime(finalElapsed)} · {finalDoneCount}/{finalTotal} exercices
              </p>
            </div>

            <div className="w-full space-y-2 max-w-xs">
              <Button
                className="w-full gap-2"
                onClick={() => {
                  endSession();
                  navigate("/dashboard");
                }}
              >
                <Home className="h-4 w-4" />
                Retour au tableau de bord
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => endSession()}
              >
                Fermer
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
